import React, { useState, useCallback } from "react";
import axios, { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";

import { validation } from "./validate";
import Loading from "components/Loading";
import { FormikTextareaField } from "components/InputsField/formikInputs/FormikTextareaField";
import Button from "components/common/Button";
import { FORUM } from "endpointsConst";
import { ForumReplyData, ForumEditData } from "types/forum";

import styles from "./styles.module.scss";
import { ERROR_PLACE } from 'components/InputsField/InputError';

interface TicketAddMessageProps {
    id?: string;
    reply: ForumReplyData | "";
    loadMessages: (explicitOffset?: number) => void;
    setOpen: (open: boolean) => void;
    setReply: (reply: ForumReplyData | "") => void;
    onSend: (messageAdded?: boolean) => void;
    editingMessage: ForumEditData | null;
    onEditMessage: (messageId: number, message: string) => Promise<void>;
    setEditingMessage: (message: ForumEditData | null) => void;
}

interface FormValues {
    message: string;
}

interface ReplyRequestData {
    message: string;
    forum_ticket_id?: string;
    message_to_reply_id?: number;
}

const TicketAddMessage: React.FC<TicketAddMessageProps> = ({
    id,
    reply,
    loadMessages,
    setOpen,
    setReply,
    onSend,
    editingMessage,
    onEditMessage,
    setEditingMessage,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const { t } = useTranslation();

    const isEditing = !!editingMessage;

    const initialValues: FormValues = {
        message: editingMessage?.message || "",
    };

    const handleClose = useCallback(() => {
        setReply("");
        if (setEditingMessage) {
            setEditingMessage(null);
        }
        return setOpen(false);
    }, [setOpen, setReply, setEditingMessage]);

    const handleSubmit = useCallback(
        async ({ message }: FormValues) => {
            setLoading(true);

            try {
                if (isEditing && onEditMessage && editingMessage) {
                    await onEditMessage(editingMessage.id, message);
                } else {
                    const data: ReplyRequestData = {
                        message,
                        forum_ticket_id: id,
                    };

                    if (reply) {
                        data.message_to_reply_id = reply.id;
                    }

                    await axios.post(FORUM + "/tickets/reply", data);
                    handleClose();
                    onSend(true);
                }
            } catch (e) {
                const error = e as AxiosError;
                console.log(error.response);
            } finally {
                setLoading(false);
            }
        },
        [id, reply, handleClose, onSend, isEditing, editingMessage, onEditMessage],
    );

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validation(t)}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            <Form className={styles.form}>
                <FormikTextareaField
                    name="message"
                    label={isEditing ? t("forums.topic.edit_message") : t("forums.topic.answer_section.heading")}
                    rows="5"
                    placeholder={t("forums.topic.answer_section.message_grey")}
                    errorMode={ERROR_PLACE.static}
                />
                {reply && !isEditing && (
                    <p className={styles.replyMessage}>
                        {t("forums.topic.if_answer_text")} {reply.replyToName}
                        <button
                            className={styles.deleteButton}
                            type="button"
                            onClick={() => setReply("")}
                        />
                    </p>
                )}
                <div className={styles.buttonContainer}>
                    <button className={styles.cancelButton} type="button" onClick={handleClose}>
                        {t("forums.topics.topic_section.cancel_button")}
                    </button>
                    {loading ? (
                        <Loading />
                    ) : (
                        <Button type="submit">
                            {isEditing ? t("save") : t("forums.topic.answer_section.send_button")}
                        </Button>
                    )}
                </div>
            </Form>
        </Formik>
    );
};

export default TicketAddMessage;
